import { useCallback, useEffect, useState } from 'react';
import { useAppSelector } from 'ducks/hooks';
import { selectParameter } from 'ducks/parameter/selectors';
import { ParameterMethodsListState } from 'ducks/parameter-methods-list/slices';
import { ReferenceNameParameters } from 'ducks/types/payload-types';
import { ProjectParameters } from 'ducks/types/store-types';
import { UnionFromArray } from 'ducks/types/type-helpers';
import { useValidate } from 'hooks/useValidation';
import { parameter_validation, parameters_default_state } from 'modals/create-parameter/constants';

export const useCreateParameterFormMethods = () => {
  const parameter = useAppSelector(selectParameter);
  const [form, setForm] = useState<UnionFromArray<ProjectParameters>>({ ...parameters_default_state });
  const { errors, validate, resetFieldError } = useValidate(form, parameter_validation);

  useEffect(() => {
    setForm({ ...parameters_default_state, ...parameter });
  }, [parameter]);

  const onChange = useCallback(
    (item: Partial<UnionFromArray<ProjectParameters>>) => {
      resetFieldError(item);
      setForm((form) => ({ ...form, ...item }));
    },
    [resetFieldError],
  );

  const onParameterMethodSelect = useCallback(
    (item: UnionFromArray<ParameterMethodsListState>) => {
      const { parameter_method_id, parameter_method_name } = item;

      if (parameter_method_id === 1) {
        onChange({
          command_line_prefix: '--',
          command_line_assignment_char: '=',
          command_line_escaped: true,
          command_line_ignore_name: false,
        });
      }

      if (parameter_method_id === 2) {
        onChange({ is_encrypted: false });
      }

      onChange({
        parameter_method_name: String(parameter_method_name),
        parameter_method_id: Number(parameter_method_id),
      });
    },
    [onChange],
  );

  const onSystemReferenceNameChange = useCallback(
    (item: ReferenceNameParameters) => {
      const { parameter_method_id, parameter_method_name, is_encrypted, is_required, system_parameter_name } = item;

      if (system_parameter_name === 'aws_access_key_id' || system_parameter_name === 'aws_secret_access_key') {
        onChange({
          is_required: true,
          is_encrypted: true,
          parameter_method_name: 'Environment Variable',
          parameter_method_id: 2,
        });
      } else {
        onChange({
          is_required: is_required,
          is_encrypted: is_encrypted,
          parameter_method_name: parameter_method_name,
          parameter_method_id: parameter_method_id,
        });
      }

      onChange({ reference_parameter_name: String(system_parameter_name) });
    },
    [onChange],
  );

  return {
    form,
    setForm,
    onChange,
    errors,
    validate,
    onParameterMethodSelect,
    onSystemReferenceNameChange,
  };
};
